import * as z from "zod";
import {
  PortalCustomization,
  portalCustomizationZodSchema,
} from "./portal-customization";
import { TranslationSource } from "./translation-source";

type Container = Record<string, unknown>;

type PathSegment = string | number;

/** Where a visited object sits in the customization. */
interface Location {
  path: readonly PathSegment[];
  /**
   * Dot-path -> value that identifies each array element on the way to the
   * object, so a write can tell the element is still at that index.
   */
  identity: Readonly<Record<string, string>>;
}

/** A translation id written to a text object, with what a write must guard. */
export interface StampedTranslationId {
  /** Dot-path of the text object, relative to the customization. */
  path: string;
  id: string;
  /** Set when a duplicate id was replaced; unset when the id was minted. */
  previousId?: string;
  /** Dot-path -> value that must still be stored for the stamp to apply. */
  expected: Readonly<Record<string, string>>;
}

export type PortalCustomizationPage = keyof NonNullable<
  PortalCustomization["pages"]
>;

const pagesSchema = portalCustomizationZodSchema.shape.pages.unwrap().unwrap();

export function isPortalCustomizationPage(
  page: string,
): page is PortalCustomizationPage {
  return page in pagesSchema.shape;
}

function isContainer(value: unknown): value is Container {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function translatableFields(schema: z.ZodObject): string[] {
  const shape: Record<string, z.core.$ZodType> = schema.shape;
  return Object.entries(shape).flatMap(([key, field]) =>
    "translatable" in field && field.translatable === true ? [key] : [],
  );
}

/** Array entries pair by their `provider` (drop-off copy), else by index. */
function pairedEntry(stored: unknown, item: unknown, index: number): unknown {
  if (!Array.isArray(stored)) {
    return undefined;
  }
  const provider = isContainer(item) ? item.provider : undefined;
  if (typeof provider === "string") {
    return stored.find(
      (entry) => isContainer(entry) && entry.provider === provider,
    );
  }
  return stored[index];
}

function nonEmptyText(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function dotPath(path: readonly PathSegment[]): string {
  return path.join(".");
}

/** The `provider`, else the first translatable text, of an array element. */
function elementIdentity(
  schema: z.core.$ZodType,
  item: unknown,
  path: readonly PathSegment[],
): Record<string, string> {
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return elementIdentity(schema.unwrap(), item, path);
  }
  if (!(schema instanceof z.ZodObject) || !isContainer(item)) {
    return {};
  }
  const provider = item.provider;
  if (typeof provider === "string") {
    return { [dotPath([...path, "provider"])]: provider };
  }
  for (const field of translatableFields(schema)) {
    const text = nonEmptyText(item[field]);
    if (text) {
      return { [dotPath([...path, field])]: text };
    }
  }
  return {};
}

/**
 * Visits each object holding `translatableText()` fields, with their keys, the
 * object at the same place in `stored`, if any, and its location in `value`.
 */
function visitTranslatableObjects(
  schema: z.core.$ZodType,
  value: unknown,
  stored: unknown,
  visit: (
    container: Container,
    fields: string[],
    stored: Container | undefined,
    location: Location,
  ) => void,
  location: Location = { path: [], identity: {} },
): void {
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    visitTranslatableObjects(schema.unwrap(), value, stored, visit, location);
    return;
  }
  if (schema instanceof z.ZodArray && Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const path = [...location.path, index];
      visitTranslatableObjects(
        schema.element,
        item,
        pairedEntry(stored, item, index),
        visit,
        {
          path,
          identity: {
            ...location.identity,
            ...elementIdentity(schema.element, item, path),
          },
        },
      );
    }
    return;
  }
  if (!(schema instanceof z.ZodObject) || !isContainer(value)) {
    return;
  }
  const storedContainer = isContainer(stored) ? stored : undefined;
  const fields = translatableFields(schema);
  const shape: Record<string, z.core.$ZodType> = schema.shape;
  for (const [key, field] of Object.entries(shape)) {
    if (!fields.includes(key)) {
      visitTranslatableObjects(
        field,
        value[key],
        storedContainer?.[key],
        visit,
        { ...location, path: [...location.path, key] },
      );
    }
  }
  if (fields.length) {
    visit(value, fields, storedContainer, location);
  }
}

function stampIds(
  customization: PortalCustomization,
  stored: unknown,
  mint: boolean,
) {
  const seen = new Set<string>();
  const stamped: StampedTranslationId[] = [];
  let minted = 0;
  let reminted = 0;
  visitTranslatableObjects(
    portalCustomizationZodSchema,
    customization,
    stored,
    (container, fields, storedContainer, { path, identity }) => {
      const record = (id: string, previousId?: string) => {
        const expected: Record<string, string> = { ...identity };
        for (const field of fields) {
          const text = nonEmptyText(container[field]);
          if (text) {
            expected[dotPath([...path, field])] = text;
            break;
          }
        }
        stamped.push({
          path: dotPath(path),
          id,
          ...(previousId === undefined ? {} : { previousId }),
          expected,
        });
      };
      const storedId = storedContainer?.id;
      if (typeof storedId === "string" && storedId) {
        container.id = storedId;
      }
      const id = container.id;
      if (typeof id === "string" && id) {
        if (seen.has(id)) {
          const remintedId = crypto.randomUUID();
          container.id = remintedId;
          record(remintedId, id);
          reminted++;
        }
      } else if (
        mint &&
        fields.some((field) => nonEmptyText(container[field]))
      ) {
        const mintedId = crypto.randomUUID();
        container.id = mintedId;
        record(mintedId);
        minted++;
      }
      const assigned = container.id;
      if (typeof assigned === "string") {
        seen.add(assigned);
      }
    },
  );
  return { minted, reminted, stamped };
}

/**
 * Mints the `id` of each text object that has text and no id, in place, and
 * remints later duplicates. Existing ids are otherwise never changed.
 */
export function stampPortalCustomizationTranslationIds(
  customization: PortalCustomization,
): { minted: number; reminted: number } {
  const { minted, reminted } = stampIds(customization, undefined, true);
  return { minted, reminted };
}

/**
 * Like `stampPortalCustomizationTranslationIds`, and also reports each id it
 * wrote with its path, so that only those ids need persisting.
 */
export function stampPortalCustomizationTranslationIdsWithPaths(
  customization: PortalCustomization,
): { minted: number; reminted: number; stamped: StampedTranslationId[] } {
  return stampIds(customization, undefined, true);
}

/**
 * Stamps an incoming customization before it replaces `stored`: an object keeps
 * the stored object's id at the same place, whatever id it arrived with.
 */
export function stampIncomingPortalCustomizationTranslationIds({
  stored,
  incoming,
}: {
  stored: unknown;
  incoming: PortalCustomization;
}): { minted: number; reminted: number } {
  const { minted, reminted } = stampIds(incoming, stored, true);
  return { minted, reminted };
}

/** Like `stampIncomingPortalCustomizationTranslationIds`, but never mints. */
export function preservePortalCustomizationTranslationIds({
  stored,
  incoming,
}: {
  stored: unknown;
  incoming: PortalCustomization;
}): { reminted: number } {
  const { reminted } = stampIds(incoming, stored, false);
  return { reminted };
}

export interface PortalTranslatableText {
  elementId: string;
  field: string;
  text: string;
}

export interface PortalCustomizationTextField {
  /** Unset until the object is saved and stamped. */
  elementId: string | undefined;
  field: string;
  text: string;
}

/** Every translatable field with text, stamped or not. */
export function portalCustomizationTextFields(
  customization: PortalCustomization | null | undefined,
  page?: PortalCustomizationPage,
): PortalCustomizationTextField[] {
  const texts: PortalCustomizationTextField[] = [];
  if (!customization) {
    return texts;
  }
  const collect = (container: Container, fields: string[]) => {
    const id = container.id;
    const elementId = typeof id === "string" && id ? id : undefined;
    for (const field of fields) {
      const text = nonEmptyText(container[field]);
      if (text) {
        texts.push({ elementId, field, text });
      }
    }
  };
  if (page === undefined) {
    visitTranslatableObjects(
      portalCustomizationZodSchema,
      customization,
      undefined,
      collect,
    );
  } else {
    visitTranslatableObjects(
      pagesSchema.shape[page],
      customization.pages?.[page],
      undefined,
      collect,
    );
  }
  return texts;
}

/** Current English of every stamped translatable field with text. */
export function portalCustomizationTranslatableText(
  customization: PortalCustomization | null | undefined,
  page?: PortalCustomizationPage,
): PortalTranslatableText[] {
  return portalCustomizationTextFields(customization, page).flatMap(
    ({ elementId, field, text }) =>
      elementId ? [{ elementId, field, text }] : [],
  );
}

export interface PortalCustomizationTranslationRecord {
  elementId: string;
  field: string;
  language: string;
  text: string;
  source: TranslationSource;
  sourceText?: string;
}

/** Element id -> field -> translated text. */
export type PortalCustomizationTextOverlay = Record<
  string,
  Record<string, string>
>;

/** Translations whose field still has the English they were written against. */
export function portalCustomizationTextOverlay({
  translations,
  customization,
  language,
}: {
  translations: readonly PortalCustomizationTranslationRecord[];
  customization: PortalCustomization | null | undefined;
  language: string;
}): PortalCustomizationTextOverlay {
  const english = new Map(
    portalCustomizationTranslatableText(customization).map((entry) => [
      `${entry.elementId}\0${entry.field}`,
      entry.text,
    ]),
  );
  const overlay: PortalCustomizationTextOverlay = {};
  for (const row of translations) {
    if (row.language !== language || !row.text) {
      continue;
    }
    const canonical = english.get(`${row.elementId}\0${row.field}`);
    if (!canonical) {
      continue;
    }
    if (row.sourceText && row.sourceText !== canonical) {
      continue;
    }
    (overlay[row.elementId] ??= {})[row.field] = row.text;
  }
  return overlay;
}

/** Overlays shopper copy onto a clone; merchant preview should skip this. */
export function applyPortalCustomizationTextOverlay(
  customization: PortalCustomization | null | undefined,
  overlay: PortalCustomizationTextOverlay,
): PortalCustomization | null {
  if (!customization) {
    return null;
  }
  if (!Object.keys(overlay).length) {
    return customization;
  }
  const next = structuredClone(customization);
  visitTranslatableObjects(
    portalCustomizationZodSchema,
    next,
    undefined,
    (container, fields) => {
      const elementId = container.id;
      const texts =
        typeof elementId === "string" ? overlay[elementId] : undefined;
      if (!texts) {
        return;
      }
      for (const field of fields) {
        const text = texts[field];
        if (text) {
          container[field] = text;
        }
      }
    },
  );
  return next;
}
